import React, {useState} from 'react';
import {Alert, Tabs, Tooltip} from 'antd';
import {
    FileTextOutlined,
    FileProtectOutlined,
} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import {useEffect, useMemo} from 'react';
import ContractViewerV2 from "./ContractViewerV2";
import ReferenceDocumentViewer from "./ReferenceDocumentViewer";
import ComparisonVersionEvidenceViewer from "./ComparisonVersionEvidenceViewer";
import {getHighlightableCommentRef, isComparisonRiskComment} from "../../../utils/comparisonRisk";
import {buildCommentCitationNavigation, canonicalCitations, citationDocumentOptions, citationHighlightBlocks} from '../../../utils/citationNavigation';
import './document.css';

const Document = ({
                      content, comments, currentVersion, onClauseHover, onClauseClick,
                      hoveredRef, selectedRef, forcedActiveTab,
                      headerActions, processingBadge, isAnalyzing = false,
                      comparisonMode = false, citationHighlights = [], includeCitationHighlights = false,
                      activeDocumentSource = 'contract', onDocumentSourceChange,
                      referenceSource = null, referenceNavigationRequest = null,
                      comparisonVersionSource = null, comparisonVersionNavigationRequest = null,
                      contractNavigationRequest = null,
                  }) => {

    const {t} = useTranslation('review');
    const [activeTab, setActiveTab] = useState('contract');
    const sourceNavigation = activeDocumentSource === 'reference' ? comparisonVersionNavigationRequest : contractNavigationRequest;
    const documentOptions = citationDocumentOptions(sourceNavigation?.comment);
    const contractCitation = activeDocumentSource === 'contract' && contractNavigationRequest?.source_kind === 'citation';
    useEffect(() => {
        if (forcedActiveTab?.key === "contract") {
            setActiveTab("contract");
        }
    }, [forcedActiveTab]);

    useEffect(() => {
        if (comparisonMode) {
            setActiveTab("contract");
        }
    }, [comparisonMode]);

    useEffect(() => {
        setActiveTab("contract");
    }, [currentVersion]);

    const handleCommentClick = (ref) => {
        setActiveTab("contract");

        if (onClauseClick) {
            onClauseClick(ref);
        }
    };

    const highlightBlocks = useMemo(() => {
        const citationBlocks = Array.isArray(citationHighlights) ? citationHighlights : [];
        if (comparisonMode) {
            return citationBlocks;
        }

        const commentBlocks = (comments || []).flatMap((comment) => {
            const highlightRef = getHighlightableCommentRef(comment);
            if (!highlightRef && isComparisonRiskComment(comment)) return [];
            const ref = highlightRef || comment.id;
            if (!ref) return [];
            if (canonicalCitations(comment).length) return citationHighlightBlocks([comment], currentVersion);
            const highlights = Array.isArray(comment.highlights) ? comment.highlights : [];
            const validHighlights = highlights
                .filter((h) => h?.bbox)
                .map((h, index) => ({
                    page: h.page ?? comment.page,
                    text: comment.title,
                    ref,
                    bbox: h.bbox,
                    kind: h.resolution_status === 'resolved' ? 'resolved_source_citation' : h.kind,
                    blockId: h.block_id,
                    highlightIndex: index,
                }));

            if (validHighlights.length > 0) return validHighlights;
            if (!comment.bbox) return [];

            return [{
                page: comment.page,
                text: comment.title,
                ref,
                bbox: comment.bbox,
                kind: "legacy_bbox",
            }];
        });

        if (!includeCitationHighlights || !selectedRef) {
            return commentBlocks;
        }

        const selectedCitationBlocks = citationBlocks.filter((citationBlock) => (
            citationBlock?.ref === selectedRef
            || citationBlock?.citation?.id === selectedRef
            || citationBlock?.blockId === selectedRef
            || citationBlock?.citation?.source_id === selectedRef
        ));

        return selectedCitationBlocks.length > 0
            ? [...commentBlocks, ...selectedCitationBlocks]
            : commentBlocks;
    }, [citationHighlights, comments, comparisonMode, includeCitationHighlights, selectedRef, currentVersion]);

    const tabItems = [
        {
            key: 'contract',
            label: (
                <div className="doc-tab-label">
                    <FileTextOutlined />
                    {t('tabs.contract')}
                </div>
            ),
            children: (
                <div className="doc-contract-view">
                    <ContractViewerV2
                        fileUrl={content.view_url}
                        textractBlocks={highlightBlocks}
                        hoveredRef={hoveredRef}
                        selectedRef={selectedRef}
                        onBlockHover={onClauseHover}
                        onBlockClick={handleCommentClick}
                        isAnalyzing={isAnalyzing}
                        isActive={activeDocumentSource === 'contract'}
                        requestedPage={contractNavigationRequest?.page}
                        requestedCitationId={contractNavigationRequest?.evidence?.source_citation_id || contractNavigationRequest?.evidence?.citation_id}
                        navigationKey={contractNavigationRequest?.requestId}
                    />
                </div>
            ),
        },
    ];

    const tabBarActions = (
        <div className="doc-tab-actions">
            {headerActions}
        </div>
    );

    const hasVersionSource = Boolean(comparisonVersionSource?.documentVersionId);
    const effectiveReferenceSource = comparisonVersionSource || referenceSource;
    const hasReferenceSource = hasVersionSource || Boolean(referenceSource?.referenceFileId && referenceSource?.reviewId);
    const secondarySourceLabel = hasVersionSource
        ? `${comparisonVersionSource.isCitation ? 'Source contractuelle' : 'Version de référence'} · ${comparisonVersionSource.title}`
        : `${t('referenceViewer.reference')} · ${referenceSource?.title || ''}`;

    return (
        <div className="doc-container">
            {contractCitation && (contractNavigationRequest.evidence?.citation_partial || contractNavigationRequest.evidence?.resolution_status !== 'resolved') && <Alert className="citation-location-notice" type="info" showIcon message="Localisation à vérifier pour ce commentaire." />}
            {documentOptions.length > 1 && <label className="citation-document-selector">Document source{' '}
                <select aria-label="Document source" value={activeDocumentSource === 'contract' ? currentVersion : comparisonVersionSource?.documentVersionId}
                    onChange={event => onDocumentSourceChange?.(buildCommentCitationNavigation(sourceNavigation.comment, event.target.value))}>
                    {documentOptions.map(option => <option key={option.id} value={option.id}>{option.label}</option>)}
                </select>
            </label>}
            {hasReferenceSource && (
                <div className="doc-source-switch" role="tablist" aria-label={t('referenceViewer.sourceSelector')}>
                    <Tooltip title={`${t('referenceViewer.contract')} · ${content?.title || ''}`}>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeDocumentSource === 'contract'}
                            className={activeDocumentSource === 'contract' ? 'is-active' : ''}
                            onClick={() => onDocumentSourceChange?.('contract')}
                        >
                            <FileTextOutlined />
                            <span>{t('referenceViewer.contract')} · {content?.title}</span>
                        </button>
                    </Tooltip>
                    <Tooltip title={secondarySourceLabel}>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={activeDocumentSource === 'reference'}
                            className={activeDocumentSource === 'reference' ? 'is-active' : ''}
                            onClick={() => onDocumentSourceChange?.('reference')}
                        >
                            <FileProtectOutlined />
                            <span>{secondarySourceLabel}</span>
                        </button>
                    </Tooltip>
                </div>
            )}
            <div className={`doc-source-panel ${activeDocumentSource === 'reference' ? 'is-hidden' : 'is-active'}`} aria-hidden={activeDocumentSource === 'reference'}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    className="doc-tabs"
                    tabBarExtraContent={{ right: tabBarActions }}
                />
            </div>
            {hasReferenceSource && (
                <div className={`doc-source-panel ${activeDocumentSource === 'reference' ? 'is-active' : 'is-hidden'}`} aria-hidden={activeDocumentSource !== 'reference'}>
                    {hasVersionSource ? (
                        <ComparisonVersionEvidenceViewer
                            source={effectiveReferenceSource}
                            navigationRequest={comparisonVersionNavigationRequest}
                            comments={comments}
                            selectedRef={selectedRef}
                            onBlockClick={handleCommentClick}
                            onBlockHover={onClauseHover}
                            isActive={activeDocumentSource === 'reference'}
                            hideCitationDocumentSelector
                        />
                    ) : (
                        <ReferenceDocumentViewer
                            source={effectiveReferenceSource}
                            navigationRequest={referenceNavigationRequest}
                        />
                    )}
                </div>
            )}
            {processingBadge && (
                <div className="doc-extraction-floating" role="status" aria-live="polite">
                    {processingBadge}
                </div>
            )}
        </div>
    );
};

export default Document;
